import React from "react";
import PropTypes from "prop-types";
import { Card, PillRow, LabelMeta, EmptyState, theme } from "../components/ui";
import { getPostingSkills } from "Common/skillHelpers";

function OverviewTab({ applicantData }) {
  if (!applicantData) {
    return <EmptyState title="No data available" icon="📭" />;
  }

  const job = applicantData.jobPosting || {};
  const isInternal = applicantData.source === 1;

  return (
    <div style={{ padding: "1.5rem" }}>
      {/* Per Sara (May-23): auto-fit collapses the 2-column layout to a
          single column on narrow viewports (mobile/tablet) so the right
          column doesn't get cramped. minmax(320px, 1fr) means: each column
          gets at least 320px before wrapping. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "1.5rem",
          alignItems: "start",
        }}
      >
        {/* LEFT — personal + documents */}
        <div>
          <Card title="Personal Information" icon="👤">
            <InfoRow label="Full Name" value={`${applicantData.firstName || ""} ${applicantData.lastName || ""}`.trim() || "—"} />
            <InfoRow label="Email" value={applicantData.email || "—"} />
            <InfoRow label="Gender" value={applicantData.gender || "—"} />
            {applicantData.forJobApply && (
              <InfoRow label="Applied For" value={applicantData.forJobApply} />
            )}
          </Card>

          <Card title="Application Documents" icon="📎">
            {!applicantData.resume && !applicantData.coverLetter ? (
              <div style={{ color: theme.textMuted, fontSize: "0.9rem" }}>
                No application documents on file.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {applicantData.resume && (
                  <DocLink
                    icon="📄"
                    label="Resume"
                    href={applicantData.resume}
                  />
                )}
                {applicantData.coverLetter && (
                  <DocLink
                    icon="✉️"
                    label="Cover Letter"
                    href={applicantData.coverLetter}
                  />
                )}
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT — job posting overview */}
        <div>
          <Card title="Job Posting Overview" icon="💼">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "12px 24px",
              }}
            >
              <InfoRow label="Job Title" value={job.jobTitle || "—"} />
              <InfoRow label="Department" value={job.departmentName || "—"} />
              <InfoRow label="Location Type" value={job.jobLocationType || "—"} />
              <InfoRow label="Job Type" value={job.jobType || "—"} />
            </div>

            {job.jobDescription && (
              <>
                <LabelMeta style={{ marginTop: "1.2rem" }}>📌 Description</LabelMeta>
                <div
                  style={{
                    marginTop: "8px",
                    background: "#f8fafd",
                    padding: "14px 16px",
                    borderRadius: "20px",
                    fontSize: "0.9rem",
                    lineHeight: 1.55,
                    color: theme.text,
                    maxHeight: "320px",
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: job.jobDescription }}
                />
              </>
            )}

            {getPostingSkills(job).length > 0 && (
              <>
                <LabelMeta style={{ marginTop: "1.2rem" }}>🛠️ Required Skills</LabelMeta>
                <PillRow items={getPostingSkills(job)} variant="skill" />
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

OverviewTab.propTypes = {
  applicantData: PropTypes.object,
};

function InfoRow({ label, value }) {
  return (
    <div style={{ marginBottom: "10px" }}>
      <div
        style={{
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          color: theme.textLabel,
          fontWeight: 600,
          marginBottom: "2px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "0.95rem", color: theme.text, fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}
InfoRow.propTypes = { label: PropTypes.string, value: PropTypes.node };

function DocLink({ icon, label, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        background: "#fafdff",
        borderLeft: "3px solid #7c9bc0",
        padding: "12px 14px",
        borderRadius: "16px",
        textDecoration: "none",
        color: theme.accentDeep,
        fontSize: "0.9rem",
        fontWeight: 500,
        transition: "background 0.15s ease",
      }}
    >
      <span style={{ fontSize: "1.2rem" }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      <span style={{ fontSize: "0.8rem", color: theme.textLabel }}>Open ↗</span>
    </a>
  );
}
DocLink.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  href: PropTypes.string,
};

export default OverviewTab;
